import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../auth/AuthProvider";
import { EngineBadge, Icon, errorText } from "../components/streaming-ui";
import { Callout, Card, EmptyState, EqBars, Page, PageHeader, Skeleton } from "../components/ui";
import { ChannelOnAir } from "./ServersPage";
import type {
  Channel,
  ChannelEngine,
  ChannelStatus,
  ChannelStreamSettings,
  ChannelWithEndpoints,
  Codec,
  OutputEndpoint,
  OutputKind,
  StreamingServer,
} from "../types";

const STATUS_LABEL: Record<ChannelStatus, string> = {
  draft: "Entwurf",
  active: "Aktiv (sendet)",
  paused: "Pausiert",
  archived: "Archiviert",
};

export function ChannelsPage(): JSX.Element {
  const [channels, setChannels] = useState<Channel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .listChannels()
      .then(setChannels)
      .catch((e) => setError(errorText(e)))
      .finally(() => setLoading(false));
  }, []);

  const onCreate = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const ch = await api.createChannel({ name, slug });
      setChannels([...channels, ch]);
      setName("");
      setSlug("");
      setShowForm(false);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Page>
      <PageHeader
        eyebrow="Sendebetrieb"
        title="Kanäle"
        description="Jeder Kanal ist ein eigenes Programm mit Streams, Live-Zugang, Sendeablauf und Cartwall."
        actions={
          <button onClick={() => setShowForm((s) => !s)} className={`btn ${showForm ? "btn-secondary" : "btn-primary"}`}>
            {showForm ? "Abbrechen" : <><Icon name="plus" /> Neuer Channel</>}
          </button>
        }
      />

      <div className="space-y-6">
        {error && <Callout tone="danger">{error}</Callout>}

        {showForm && (
          <form onSubmit={onCreate} className="card animate-fade-in">
            <div className="card-header"><h2 className="card-title">Neuen Kanal anlegen</h2></div>
            <div className="card-body space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <label className="block">
                  <span className="field-label">Name</span>
                  <input
                    className="input"
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      if (!slug) setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]+/g, "-").replace(/^-+|-+$/g, ""));
                    }}
                    required
                    minLength={2}
                    maxLength={80}
                  />
                </label>
                <label className="block">
                  <span className="field-label">Slug</span>
                  <input
                    className="input font-mono"
                    value={slug}
                    onChange={(e) => setSlug(e.target.value)}
                    required
                    pattern="[a-z0-9-]+"
                    minLength={2}
                    maxLength={40}
                  />
                </label>
              </div>
              <p className="field-hint">
                Neue Kanäle bekommen MP3 und AAC bei Icecast, MP3 bei SHOUTcast und HLS. Gesendet wird, sobald der Kanal auf „Aktiv" steht.
              </p>
              <button type="submit" disabled={busy} className="btn btn-primary">
                {busy ? "Erstellen …" : "Erstellen"}
              </button>
            </div>
          </form>
        )}

        {loading ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            <Skeleton className="h-52" />
            <Skeleton className="h-52" />
          </div>
        ) : channels.length === 0 ? (
          <EmptyState
            icon={<Icon name="music" />}
            title="Noch keine Channels."
            description="Lege den ersten Kanal an – Streams und Live-Zugang richtet die Suite von selbst ein."
            action={<button onClick={() => setShowForm(true)} className="btn btn-primary"><Icon name="plus" /> Neuer Channel</button>}
          />
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {channels.map((c) => (
              <ChannelCard key={c.id} channel={c} />
            ))}
          </div>
        )}
      </div>
    </Page>
  );
}

/** Schnellwege in die Unterseiten eines Kanals. */
const CHANNEL_LINKS: Array<[string, string]> = [
  ["", "Einstellungen & Ausgänge"],
  ["/rundown", "Sendeablauf"],
  ["/playout", "Playlists & Cartwall"],
  ["/schedule", "Sendeplan"],
  ["/podcast", "Podcast"],
  // f-web: eigene Hörerseite des Kanals (pages/KanalHoererseitePage.tsx)
  ["/hoererseite", "Hörerseite"],
];

/** Die Reiter eines Kanals; `aktiv` ist der Pfad-Anhang der gerade offenen Unterseite. */
export function KanalReiter({ slug, aktiv }: { slug: string; aktiv: string }): JSX.Element {
  return (
    <nav className="-mb-px flex gap-1 overflow-x-auto border-b border-default text-sm">
      {CHANNEL_LINKS.map(([suffix, label]) => (
        <Link
          key={suffix}
          to={`/channels/${slug}${suffix}`}
          aria-current={suffix === aktiv ? "page" : undefined}
          className={`whitespace-nowrap border-b-2 px-3 py-2 font-medium ${suffix === aktiv ? "border-brand-primary text-primary" : "border-transparent text-muted hover:text-primary"}`}
        >
          {label}
        </Link>
      ))}
    </nav>
  );
}

function ChannelCard({ channel }: { channel: Channel }): JSX.Element {
  const [engine, setEngine] = useState<ChannelEngine | null>(null);

  useEffect(() => {
    api.getChannelEngine(channel.slug).then(setEngine).catch(() => setEngine(null));
  }, [channel.slug]);

  const running = channel.status === "active" && engine?.engine.state === "running";
  const np = engine?.now_playing ?? null;
  const hoerer = engine ? engine.streams.reduce((sum, s) => sum + (s.listeners ?? 0), 0) : null;
  const streams = engine ? engine.streams.filter((s) => s.enabled).length : null;

  return (
    <section className={`card group flex flex-col overflow-hidden transition-shadow hover:shadow-md ${running ? "ring-1 ring-brand-primary/25" : ""}`}>
      <div className={`relative px-5 pb-4 pt-5 ${running ? "brand-glow" : ""}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <span className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl text-base font-bold ${running ? "bg-brand-primary text-white shadow-glow" : "bg-input text-secondary ring-1 ring-[var(--border)]"}`}>
              {channel.name.slice(0, 1).toUpperCase()}
            </span>
            <div className="min-w-0">
              <Link to={`/channels/${channel.slug}`} className="block truncate text-[0.95rem] font-semibold text-primary hover:text-brand-primary">
                {channel.name}
              </Link>
              <div className="truncate font-mono text-xs text-muted">/{channel.slug}</div>
            </div>
          </div>
          <EngineBadge state={engine?.engine.state ?? null} active={channel.status === "active"} />
        </div>

        {/* Was gerade läuft */}
        <div className="mt-4 flex h-9 min-w-0 items-center gap-2 rounded-lg bg-input px-3 text-sm ring-1 ring-[var(--border)]">
          {running && np ? (
            <>
              {np.on_air === "live" ? (
                <span className="inline-flex flex-shrink-0 items-center gap-1 rounded bg-red-600 px-1.5 py-px text-[0.65rem] font-bold uppercase tracking-wider text-white">Live</span>
              ) : (
                <EqBars className="flex-shrink-0 text-brand-primary" />
              )}
              <span className="truncate text-primary">{[np.artist, np.title].filter(Boolean).join(" – ") || "ohne Titelangabe"}</span>
            </>
          ) : (
            <span className="truncate text-muted">{channel.status === "active" ? "Gerade kein Titel gemeldet" : "Sendet nicht"}</span>
          )}
        </div>

        {/* Kennzahlen */}
        <dl className="mt-4 grid grid-cols-3 gap-2 text-center">
          <div>
            <dt className="text-[0.7rem] text-muted">Hörer</dt>
            <dd className="text-lg font-semibold tabular-nums text-primary">{hoerer ?? "–"}</dd>
          </div>
          <div className="border-x border-default">
            <dt className="text-[0.7rem] text-muted">Streams</dt>
            <dd className="text-lg font-semibold tabular-nums text-primary">{streams ?? "–"}</dd>
          </div>
          <div>
            <dt className="text-[0.7rem] text-muted">Status</dt>
            <dd className="truncate pt-1 text-xs font-medium text-secondary">{STATUS_LABEL[channel.status]}</dd>
          </div>
        </dl>
      </div>

      <div className="mt-auto flex flex-wrap gap-1 border-t border-default bg-[var(--bg-sunken)] px-3 py-2.5">
        {CHANNEL_LINKS.map(([suffix, label]) => (
          <Link key={suffix} to={`/channels/${channel.slug}${suffix}`} className="rounded-md px-2 py-1 text-xs font-medium text-secondary hover:bg-card hover:text-primary hover:shadow-sm">
            {label}
          </Link>
        ))}
      </div>
    </section>
  );
}

export function ChannelDetailPage({ slug }: { slug: string }): JSX.Element {
  const { user } = useAuth();
  const isAdmin = user?.role === "tenant_admin" || user?.role === "super_admin";
  const [data, setData] = useState<ChannelWithEndpoints | null>(null);
  const [engine, setEngine] = useState<ChannelEngine | null>(null);
  const [servers, setServers] = useState<StreamingServer[]>([]);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const [ch, eng] = await Promise.all([api.getChannel(slug), api.getChannelEngine(slug)]);
      setData(ch);
      setEngine(eng);
    } catch (e) {
      setError(errorText(e));
    }
  }, [slug]);

  useEffect(() => {
    void refresh();
    api.listServers().then(setServers).catch(() => setServers([]));
    const t = setInterval(() => void refresh(), 5000);
    return () => clearInterval(t);
  }, [refresh]);

  if (!data) {
    return (
      <Page>
        {error ? <Callout tone="danger">{error}</Callout> : <div className="space-y-4"><Skeleton className="h-16 w-72" /><Skeleton className="h-72" /></div>}
      </Page>
    );
  }

  const setStatus = async (status: ChannelStatus): Promise<void> => {
    try {
      await api.updateChannel(data.id, { status });
      await refresh();
    } catch (err) {
      setError(errorText(err));
    }
  };

  return (
    <Page>
      <PageHeader
        eyebrow={<Link to="/channels" className="hover:text-brand-primary">Kanäle</Link>}
        title={
          <span className="flex flex-wrap items-center gap-3">
            {data.name}
            <EngineBadge state={engine?.engine.state ?? null} active={data.status === "active"} />
          </span>
        }
        description={<span className="font-mono">/{data.slug}</span>}
        actions={
          <>
            {isAdmin && (
              <label className="flex items-center gap-2 text-sm text-secondary">
                <span className="sr-only sm:not-sr-only">Status</span>
                <select className="input !w-auto font-medium" value={data.status} onChange={(e) => void setStatus(e.target.value as ChannelStatus)}>
                  {(Object.keys(STATUS_LABEL) as ChannelStatus[]).map((s) => (
                    <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                  ))}
                </select>
              </label>
            )}
          </>
        }
      >
        <KanalReiter slug={data.slug} aktiv="" />
      </PageHeader>

      <div className="space-y-6">
        {error && <Callout tone="danger">{error}</Callout>}

        {engine && (
          <ChannelOnAir
            channel={{ id: data.id, slug: data.slug, name: data.name, status: data.status, engine: engine.engine, now_playing: engine.now_playing, ingest: engine.ingest, streams: engine.streams }}
            canAdmin={isAdmin}
            onChanged={refresh}
          />
        )}

        {isAdmin && engine && <StreamSettingsForm slug={slug} initial={engine.stream_settings} />}

        <Card title="Ausgänge" description="Änderungen gelten sofort: Nur der betroffene Ausgang verbindet sich neu, alle anderen Streams laufen weiter." padded={false}>
          <EndpointTable endpoints={data.endpoints} canEdit={isAdmin} onChanged={refresh} onError={setError} />
          {isAdmin && <div className="border-t border-default px-5 py-4"><AddEndpointForm channelId={data.id} servers={servers} onCreated={refresh} /></div>}
        </Card>
      </div>
    </Page>
  );
}

function StreamSettingsForm({ slug, initial }: { slug: string; initial: ChannelStreamSettings }): JSX.Element {
  const [form, setForm] = useState<ChannelStreamSettings>(initial);
  const [state, setState] = useState<"idle" | "saving" | "saved" | string>("idle");

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setState("saving");
    try {
      await api.saveStreamSettings(slug, {
        genre: form.genre || undefined,
        description: form.description || undefined,
        website: form.website || undefined,
      });
      setState("saved");
    } catch (err) {
      setState(errorText(err));
    }
  };

  return (
    <form onSubmit={submit} className="card">
      <div className="card-header">
        <div>
          <h2 className="card-title">Stream-Angaben</h2>
          <p className="mt-0.5 text-xs text-muted">Diese Angaben sehen Hörer in ihrem Player und in Stream-Verzeichnissen.</p>
        </div>
      </div>
      <div className="card-body">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <label className="block text-[0.8rem] font-medium text-secondary">
          Genre
          <input className="input mt-1.5" value={form.genre ?? ""} onChange={(e) => setForm({ ...form, genre: e.target.value })} maxLength={60} placeholder="z. B. Pop, Rock" />
        </label>
        <label className="block text-[0.8rem] font-medium text-secondary sm:col-span-2">
          Webseite
          <input type="url" className="input mt-1.5" value={form.website ?? ""} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://" />
        </label>
        <label className="block text-[0.8rem] font-medium text-secondary sm:col-span-3">
          Beschreibung
          <input className="input mt-1.5" value={form.description ?? ""} onChange={(e) => setForm({ ...form, description: e.target.value })} maxLength={200} />
        </label>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button type="submit" disabled={state === "saving"} className="btn btn-primary">
          Speichern
        </button>
        {state === "saved" && <span className="text-xs font-medium text-emerald-700 dark:text-emerald-400">Gespeichert. Die Streams verbinden sich mit den neuen Angaben neu (ein kurzer Moment für die Hörer).</span>}
        {state !== "idle" && state !== "saving" && state !== "saved" && <span className="text-xs text-red-600 dark:text-red-400">{state}</span>}
      </div>
      </div>
    </form>
  );
}

function EndpointTable({ endpoints, canEdit, onChanged, onError }: {
  endpoints: OutputEndpoint[]; canEdit: boolean; onChanged: () => Promise<void>; onError: (msg: string) => void;
}): JSX.Element {
  if (endpoints.length === 0) {
    return <div className="px-5 py-8 text-center text-sm text-muted">Keine Ausgänge. Ohne Ausgang läuft der Kanal, erreicht aber niemanden.</div>;
  }
  const act = async (fn: () => Promise<unknown>): Promise<void> => {
    try {
      await fn();
      await onChanged();
    } catch (err) {
      onError(errorText(err));
    }
  };
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left">
            <th className="py-2.5 pl-5 pr-3">Art</th>
            <th className="px-3">Server</th>
            <th className="px-3">Format</th>
            <th className="px-3">Mount</th>
            <th className="px-3">Hörer-Adresse</th>
            <th className="px-3">Aktiv</th>
            {canEdit && <th className="pr-5" />}
          </tr>
        </thead>
        <tbody>
          {endpoints.map((e) => (
            <tr key={e.id} className={`[&>td]:border-t [&>td]:border-default ${e.enabled ? "" : "opacity-60"}`}>
              <td className="py-2.5 pl-5 pr-3 font-medium text-primary">{e.kind === "hls" ? "HLS" : e.kind === "icecast" ? "Icecast" : `SHOUTcast #${e.sid}`}</td>
              <td className="px-3 text-secondary">{e.kind === "hls" ? "Zuhörerseite" : e.server_name ?? <span className="badge badge-danger">fehlt</span>}</td>
              <td className="whitespace-nowrap px-3 tabular-nums text-secondary">
                {e.codec.toUpperCase().replace("_PLUS", "+")} · {e.kind === "hls" ? `${(e.hls_variants ?? [e.bitrate_kbps]).join("/")} kbps` : `${e.bitrate_kbps} kbps`}
              </td>
              <td className="px-3 font-mono text-xs text-secondary">{e.mount ?? "—"}</td>
              <td className="max-w-xs truncate px-3 font-mono text-xs">
                {e.listen_url ? (
                  <a href={e.listen_url} target="_blank" rel="noreferrer" className="text-muted hover:text-brand-primary hover:underline">{e.listen_url}</a>
                ) : "—"}
              </td>
              <td className="px-3">
                {canEdit ? (
                  <input type="checkbox" checked={e.enabled} onChange={() => void act(() => api.updateEndpoint(e.id, { enabled: !e.enabled }))} />
                ) : e.enabled ? "✓" : "—"}
              </td>
              {canEdit && (
                <td className="pr-5 text-right">
                  <button
                    onClick={() => {
                      if (window.confirm("Ausgang löschen? Hörer auf diesem Stream werden getrennt.")) void act(() => api.deleteEndpoint(e.id));
                    }}
                    className="btn btn-ghost btn-sm text-red-600 dark:text-red-400"
                  >
                    Löschen
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const CODECS: Record<OutputKind, Array<{ value: Codec; label: string }>> = {
  icecast: [
    { value: "mp3", label: "MP3" },
    { value: "aac", label: "AAC" },
    { value: "aac_plus", label: "HE-AAC (AAC+)" },
    { value: "opus", label: "Opus" },
    { value: "ogg", label: "Ogg Vorbis" },
  ],
  shoutcast: [
    { value: "mp3", label: "MP3" },
    { value: "aac", label: "AAC" },
    { value: "aac_plus", label: "HE-AAC (AAC+)" },
  ],
  hls: [{ value: "aac", label: "AAC" }],
};

function AddEndpointForm({ channelId, servers, onCreated }: { channelId: string; servers: StreamingServer[]; onCreated: () => Promise<void> }): JSX.Element {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<OutputKind>("icecast");
  const [serverId, setServerId] = useState("");
  const [codec, setCodec] = useState<Codec>("mp3");
  const [bitrate, setBitrate] = useState(128);
  const [mount, setMount] = useState("");
  const [variants, setVariants] = useState("64,128");
  const [error, setError] = useState<string | null>(null);

  const candidates = servers.filter((s) => s.kind === kind);

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="btn btn-secondary">
        <Icon name="plus" /> Ausgang hinzufügen
      </button>
    );
  }

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    try {
      await api.createEndpoint(channelId, kind === "hls"
        ? { kind, codec: "aac", bitrate_kbps: bitrate, hls_variants: variants.split(",").map((v) => Number(v.trim())).filter(Boolean) }
        : { kind, server_id: serverId || candidates[0]?.id, codec, bitrate_kbps: bitrate, mount });
      setOpen(false);
      setMount("");
      await onCreated();
    } catch (err) {
      setError(errorText(err));
    }
  };

  return (
    <form onSubmit={submit} className="grid grid-cols-1 gap-4 sm:grid-cols-3">
      <label className="block text-[0.8rem] font-medium text-secondary">
        Art
        <select className="input mt-1.5" value={kind} onChange={(e) => {
          const k = e.target.value as OutputKind;
          setKind(k);
          setCodec(CODECS[k][0]!.value);
          setServerId("");
        }}>
          <option value="icecast">Icecast</option>
          <option value="shoutcast">SHOUTcast</option>
          <option value="hls">HLS (Browser, Smartphone)</option>
        </select>
      </label>
      {kind !== "hls" && (
        <label className="block text-[0.8rem] font-medium text-secondary">
          Server
          <select className="input mt-1.5" value={serverId || candidates[0]?.id || ""} onChange={(e) => setServerId(e.target.value)} required>
            {candidates.length === 0 && <option value="">kein Server dieser Art</option>}
            {candidates.map((s) => (
              <option key={s.id} value={s.id}>{s.name}{s.managed ? " (Suite)" : " (extern)"}</option>
            ))}
          </select>
        </label>
      )}
      <label className="block text-[0.8rem] font-medium text-secondary">
        Format
        <select className="input mt-1.5" value={codec} onChange={(e) => setCodec(e.target.value as Codec)}>
          {CODECS[kind].map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
      </label>
      {kind === "hls" ? (
        <label className="block text-[0.8rem] font-medium text-secondary sm:col-span-2">
          Bitraten (kbps, kommagetrennt)
          <input className="input mt-1.5" value={variants} onChange={(e) => setVariants(e.target.value)} pattern="[0-9 ,]+" />
        </label>
      ) : (
        <>
          <label className="block text-[0.8rem] font-medium text-secondary">
            Bitrate (kbps)
            <input type="number" min={32} max={320} className="input mt-1.5" value={bitrate} onChange={(e) => setBitrate(Number(e.target.value))} />
          </label>
          <label className="block text-[0.8rem] font-medium text-secondary sm:col-span-2">
            Mount
            <input className="input mt-1.5 font-mono" value={mount} onChange={(e) => setMount(e.target.value)} placeholder="/meinradio.mp3" pattern="/[A-Za-z0-9._\-/]+" required />
            <span className="field-hint block font-normal">{kind === "shoutcast" ? "Pfad des Streams; die Stream-Nummer vergibt die Suite." : "Beginnt mit /, auf dem Server eindeutig."}</span>
          </label>
        </>
      )}
      {error && <div className="sm:col-span-3"><Callout tone="danger">{error}</Callout></div>}
      <div className="flex gap-2 sm:col-span-3">
        <button type="submit" className="btn btn-primary">Anlegen</button>
        <button type="button" onClick={() => setOpen(false)} className="btn btn-ghost">Abbrechen</button>
      </div>
    </form>
  );
}
